<template>
  <div :class="`${keyboardClass} vkey_${inputName}`"></div>
</template>

<script>
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";

import frenchLayout from "simple-keyboard-layouts/build/layouts/french";
import englishLayout from "simple-keyboard-layouts/build/layouts/english";
import germanLayout from "simple-keyboard-layouts/build/layouts/german";
import italianLayout from "simple-keyboard-layouts/build/layouts/italian";

const determineLocaleBasedKeyboardLayout = (lang_name) => {
  switch (lang_name.toLowerCase()) {
    case "English":
      return englishLayout;
    case "German":
      return germanLayout;
    case "Italian":
      return italianLayout;
    case "French":
      return frenchLayout;
    default:
      return englishLayout;
  }
};

// TODO(mushaheedx): Get client's language name for use here.
const keyboardLayout = determineLocaleBasedKeyboardLayout("English");

export default {
  name: "SimpleKeyboard",
  props: {
    keyboardClass: {
      default: "simple-keyboard",
      type: String,
    },
    input: {
      type: String,
    },
    inputName: {
      type: String,
      default: "Default",
    },
    keyBoardType: {
      type: String,
      default: "Default",
    },
  },
  data: () => ({
    keyboard: null,
    shiftActive: false,
    capsLockActive: false,
  }),
  mounted() {
    let keyBoardOptions = {
      onChange: this.onChange,
      onKeyPress: this.onKeyPress,
      inputName: this.inputName,
    };

    if (this.keyBoardType == "numeric") {
      keyBoardOptions["layout"] = {
        default: ["1 2 3", "4 5 6", "7 8 9", ". 0 {bksp}", "{close}"],
      };
      keyBoardOptions["mergeDisplay"] = true;
      keyBoardOptions["display"] = {
        "{close}": "Close",
      };
      keyBoardOptions["theme"] =
        "hg-theme-default hg-layout-numeric numeric-theme w-25 mx-auto";
    } else {
      keyBoardOptions["layout"] = {
        default: [
          "` 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} q w e r t y u i o p [ ] \\",
          "{lock} a s d f g h j k l ; ' {enter}",
          "{shift} z x c v b n m , . / {shift}",
          ".com @ {space} {close}",
        ],
        shift: [
          "~ ! @ # $ % ^ &amp; * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P { } |",
          '{lock} A S D F G H J K L : " {enter}',
          "{shift} Z X C V B N M &lt; &gt; ? {shift}",
          ".com @ {space} {close}",
        ],
      };
      keyBoardOptions["mergeDisplay"] = true;
      keyBoardOptions["display"] = {
        "{close}": "Close",
        "{enter}": "Enter",
      };

      keyBoardOptions["buttonTheme"] = [
        {
          class: "closeBtn",
          buttons: "{close}",
        },
      ];
    }

    this.keyboard = new Keyboard(`.vkey_${this.inputName}`, keyBoardOptions);
    this.keyboard.setInput(this.input);
  },
  methods: {
    onChange(input) {
      this.$emit("onChange", input);
    },
    onKeyPress(button) {
      this.$emit("onKeyPress", button);

      if (button === "{shift}") {
        this.shiftActive = true;
        this.keyboard.setOptions({
          layoutName: this.capsLockActive ? "default" : "shift",
        });
        return;
      }

      if (button === "{lock}") {
        this.capsLockActive = !this.capsLockActive;
        this.shiftActive = false;
        this.keyboard.setOptions({
          layoutName: this.capsLockActive ? "shift" : "default",
        });
        return;
      }

      if (this.shiftActive && !this.isControlKey(button)) {
        this.shiftActive = false;
        setTimeout(() => {
          this.keyboard.setOptions({
            layoutName: this.capsLockActive ? "shift" : "default",
          });
        }, 0);
      }
    },
    isControlKey(button) {
      return [
        "{bksp}",
        "{tab}",
        "{enter}",
        "{close}",
        "{shift}",
        "{lock}",
      ].includes(button);
    },
  },
  watch: {
    input(input) {
      this.keyboard.setInput(input);
    },
    inputName(inputName) {
      this.keyboard.setOptions({ inputName });
    },
  },
};
</script>

<style>
.hg-theme-default {
  padding: 0 !important;
  border-radius: 16px !important;
  background-color: transparent !important;
}

.hg-theme-default .hg-button {
  height: 46px !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  color: #1f2937 !important;
  font-size: 15px;
  font-weight: 700;
  background: #ffffff !important;
  box-shadow: none !important;
  transition: background 0.14s ease, border-color 0.14s ease, color 0.14s ease;
}

.hg-theme-default .hg-button:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

.hg-theme-default .hg-button:active {
  color: #ffffff !important;
  background: var(--blue) !important;
}

.hg-theme-default .hg-row {
  gap: 7px;
  margin-bottom: 7px;
}

.hg-theme-default .hg-row:last-child {
  margin-bottom: 0;
}

.hg-theme-default .hg-button-space {
  max-width: none !important;
}

.hg-theme-default .hg-button-bksp,
.hg-theme-default .hg-button-enter,
.hg-theme-default .hg-button-shift,
.hg-theme-default .hg-button-lock,
.hg-theme-default .hg-button-tab {
  background: #f8fafc !important;
}

.hg-theme-default .closeBtn {
  color: #ffffff !important;
  background: var(--blue) !important;
  border-color: var(--blue) !important;
}

.closeBtn {
  max-width: 220px;
}

@media (max-width: 900px), (max-height: 720px) {
  .hg-theme-default .hg-button {
    height: 40px !important;
    border-radius: 10px !important;
    font-size: 13px;
  }

  .hg-theme-default .hg-row {
    gap: 5px;
    margin-bottom: 5px;
  }
}

@media (max-height: 640px) {
  .hg-theme-default .hg-button {
    height: 34px !important;
    font-size: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/UIElements/SimpleKeyboard.vue